import React from 'react'
import ReactDOM from 'react-dom/client'
import { installApiShim } from './api-shim'
import { bootTheme } from './lib/themeStore'

// 1. Install the relay-backed API shim BEFORE any component reads window.api
installApiShim()
// Apply the user's saved light/dark/system choice as early as possible
// so the picker and any subsequently-mounted desktop app render at the
// right theme without a flash. Subscribes to OS changes when set to
// "system".
bootTheme()

import { useConnectionStore } from './stores/connectionStore'
import { relay } from './lib/relay'
import { isLocalRelayHost } from '@shared/relay-protocol'
import { setTunnelResolver } from '@app/lib/terminalLinks'

/**
 * The Supabase access token, cached so the tunnel resolver can read it.
 *
 * `TunnelResolver` MUST be synchronous: the URL it produces is handed to
 * `window.open()` inside the click gesture, and Safari blocks a popup opened
 * after an await. `supabase.auth.getSession()` is async, so the token is kept
 * here and refreshed out of band instead.
 */
let cachedAccessToken: string | null = null

/**
 * Point the shared terminal-link handler at this client's relay tunnel.
 *
 * Reads the connection store at CALL time rather than closing over a
 * snapshot, so pairing with a different desktop — or unpairing, which clears
 * channelId and makes the resolver return null — takes effect immediately.
 */
function installTunnelResolver(): void {
  void (async () => {
    try {
      const { supabase } = await import('@framework')
      const { data } = await supabase.auth.getSession()
      cachedAccessToken = data.session?.access_token ?? null
      supabase.auth.onAuthStateChange((_evt: unknown, session: { access_token?: string } | null) => {
        cachedAccessToken = session?.access_token ?? null
      })
    } catch {
      // No session is not an error here: a tunnel link still works for a
      // caller that can set an Authorization header, and the relay answers a
      // browser without one with a clear 401 rather than silence.
    }
  })()

  setTunnelResolver(() => {
    const { channelId, host } = useConnectionStore.getState()
    if (!channelId || !host) return null
    return {
      relayOrigin: `${isLocalRelayHost(host) ? 'http' : 'https'}://${host}`,
      channelId,
      accessToken: cachedAccessToken,
    }
  })
}

// Routing is URL-driven, not pairing-driven:
//   /auth-callback        → handle auth, redirect to /
//   /server/:channelId    → connect to that server, load the desktop app
//   anything else (incl /) → show the server list
//
// Saved pairing in localStorage is just a cache that lets /server/:id
// skip a Supabase round-trip — it never auto-redirects on its own.

async function boot() {
  const root = ReactDOM.createRoot(document.getElementById('root')!)
  const path = window.location.pathname

  if (path === '/auth-callback') {
    const hash = window.location.hash.substring(1)
    const params = new URLSearchParams(hash)
    const accessToken = params.get('access_token')
    if (accessToken) localStorage.setItem('codeflows-access-token', accessToken)
    window.location.href = '/'
    return
  }

  const serverMatch = path.match(/^\/server\/([0-9a-f-]+)\/?$/i)
  if (serverMatch) {
    await mountServer(root, serverMatch[1])
    return
  }

  await mountServerList(root)
}

async function mountServer(root: ReactDOM.Root, channelId: string) {
  // Find the requested server in Supabase to get its key + verify the
  // user owns it. Falls back to the cached pairing if the lookup fails
  // and the cached channel matches the URL.
  const store = useConnectionStore.getState()
  let pairing: { channelId: string; channelKeyHex: string; host: string } | null = null
  try {
    const { supabase } = await import('@framework')
    const { data: session } = await supabase.auth.getSession()
    if (session?.session) {
      const { data: servers } = await supabase.rpc('codeflows_server_list')
      if (Array.isArray(servers)) {
        const server = servers.find((s: any) => s.channel_id === channelId)
        if (server) {
          pairing = {
            channelId: server.channel_id,
            channelKeyHex: server.channel_key_hex,
            host: 'codeflows-relay.shaun-395.workers.dev',
          }
        }
      }
    }
  } catch { /* ignore — fall back to cache */ }

  if (!pairing && store.channelId === channelId && store.channelKey && store.host) {
    pairing = { channelId: store.channelId, channelKeyHex: store.channelKey, host: store.host }
  }

  if (!pairing) {
    await import('./index.css')
    showDisconnected(root, 'Server not found')
    return
  }

  if (store.channelId !== pairing.channelId || store.channelKey !== pairing.channelKeyHex || store.host !== pairing.host) {
    store.disconnect()
    store.setPairingInfo(pairing.channelId, pairing.channelKeyHex, pairing.host)
  }
  await store.connect().catch(() => {})

  // Teach the shared terminal-link handler where this client's tunnel is.
  //
  // Without this, `setTunnelResolver` had no caller anywhere in the repo:
  // `currentTunnel()` always returned null, `rewriteToTunnel` was never
  // reached, and clicking `http://localhost:5173` in a relayed terminal fell
  // through to an in-app `browser` tab — which renders an Electron <webview>
  // and is simply blank in a real browser, pointed at the PHONE's localhost.
  // The whole tunnel (worker route, localhostProxy, per-port opt-in) sat
  // behind a switch nothing flipped.
  installTunnelResolver()

  const connected = await waitForConnection()
  if (!connected) {
    await import('./index.css')
    showDisconnected(root, 'Could not connect to desktop')
    return
  }

  try {
    // Same webfont the desktop loads in app/src/main.tsx — without it the
    // relayed UI falls back to the system sans and every metric shifts.
    await Promise.all([
      import('@fontsource/open-sans/400.css'),
      import('@fontsource/open-sans/500.css'),
      import('@fontsource/open-sans/600.css'),
      import('@fontsource/open-sans/700.css'),
    ])
    await import('@app/index.css')
    // Theme was applied at module load by bootTheme() — don't reset it
    // to system here, the user may have picked dark/light from the picker.
    const App = (await import('@app/App')).default
    root.render(<React.StrictMode><App /></React.StrictMode>)
    watchConnection()
  } catch (err) {
    console.error('Failed to load app:', err)
    await import('./index.css')
    showDisconnected(root, 'Failed to load app')
  }
}

async function mountServerList(root: ReactDOM.Root) {
  // Showing the picker means we don't want a stale relay connection
  // running in the background.
  useConnectionStore.getState().disconnect()
  // Load the desktop app's stylesheet so the picker shares the same
  // tokens, .sidebar-item, .btn-ghost, .shadow-panel etc. — gives the
  // picker the exact look-and-feel of the rest of the app. The mobile
  // index.css follows for safe-area variables and a couple of mobile-
  // specific rules.
  await import('@app/index.css')
  await import('./index.css')
  // Theme was applied at module load by bootTheme().
  const { UserProvider } = await import('@framework')
  const ServersPage = (await import('./pages/ServersPage')).default
  root.render(
    <React.StrictMode>
      <UserProvider>
        <ServersPage />
      </UserProvider>
    </React.StrictMode>
  )
}

/**
 * Wait for WebSocket to connect and verify with a real request.
 * Returns true if connected and working, false if timed out.
 */
async function waitForConnection(): Promise<boolean> {
  // Wait for WebSocket open (max 10s)
  const wsOpen = await new Promise<boolean>((resolve) => {
    if (useConnectionStore.getState().connected) { resolve(true); return }
    const unsub = useConnectionStore.subscribe((s) => {
      if (s.connected) { unsub(); resolve(true) }
    })
    setTimeout(() => { unsub(); resolve(false) }, 10000)
  })

  if (!wsOpen) return false

  // Verify with a real request (get-projects should always work)
  try {
    await relay.request({ type: 'get-projects', args: [] }, 5000)
    return true
  } catch {
    return false
  }
}

/**
 * Watch for disconnection and show a reconnect overlay.
 * The overlay sits on TOP of the running React app (not inside it).
 */
function watchConnection() {
  let overlayEl: HTMLDivElement | null = null

  const showOverlay = () => {
    if (overlayEl) return
    overlayEl = document.createElement('div')
    overlayEl.id = 'mobile-reconnect-overlay'
    overlayEl.innerHTML = `
      <div style="position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.8);backdrop-filter:blur(4px)">
        <div style="text-align:center;color:#fafafa">
          <div style="width:32px;height:32px;border:2px solid #3f3f46;border-top:2px solid #fafafa;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px"></div>
          <p style="font-size:14px;margin-bottom:4px">Connection lost</p>
          <p style="font-size:12px;color:#71717a;margin-bottom:16px">Reconnecting to desktop...</p>
          <button onclick="window.location.reload()" style="padding:8px 20px;background:#27272a;border:1px solid #3f3f46;border-radius:6px;color:#fafafa;font-size:13px;cursor:pointer">
            Retry
          </button>
        </div>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    `
    document.body.appendChild(overlayEl)
  }

  const hideOverlay = () => {
    if (overlayEl) {
      overlayEl.remove()
      overlayEl = null
    }
  }

  // Listen for connection state changes
  useConnectionStore.subscribe((s) => {
    if (!s.connected) {
      showOverlay()
    } else {
      hideOverlay()
    }
  })
}

/**
 * Show disconnected state (before app loads)
 */
function showDisconnected(root: ReactDOM.Root, message: string) {
  root.render(
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0a0a', color: '#fafafa', gap: '16px' }}>
      <div style={{ width: '24px', height: '24px', border: '2px solid #3f3f46', borderTop: '2px solid #fafafa', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ fontSize: '14px', color: '#a1a1aa' }}>{message}</p>
      <p style={{ fontSize: '12px', color: '#52525b' }}>Make sure CodeFlows is running on your computer</p>
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 16px', background: '#27272a', border: '1px solid #3f3f46', borderRadius: '6px', color: '#fafafa', fontSize: '13px', cursor: 'pointer' }}
        >
          Retry
        </button>
        <button
          onClick={() => { useConnectionStore.getState().unpair(); window.location.href = '/' }}
          style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #3f3f46', borderRadius: '6px', color: '#a1a1aa', fontSize: '13px', cursor: 'pointer' }}
        >
          Back
        </button>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

boot()
